import { Injectable } from '@angular/core';
import { MedicalRecord } from '../models/medical-record';
import { AuxiliarService } from './auxiliar.service';
import { MedicalRecordDrawerComponent } from '../components/drawers/medical-record-drawer/medical-record-drawer.component';
import { TranslateService } from '@ngx-translate/core';
import { Patient } from '../models/patient';
import { from, Observable, of, switchMap } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import _ from 'lodash';
import { CloseMedicalRecordDrawerComponent } from '../components/drawers/close-medical-record-drawer/close-medical-record-drawer.component';
import { TenantService } from './tenant.service';
import { DocDrawerComponent } from '../components/drawers/doc-drawer/doc-drawer.component';
import { Doc } from '../models/document';

@Injectable({
  providedIn: 'root'
})
export class MedicalRecordService {

  constructor(
    private auxiliarService: AuxiliarService,
    private translate: TranslateService,
    private httpClient: HttpClient,
    private tenantService: TenantService,
  ) { }

  // [GET] get medical record
  public getMedicalRecord(idMedicalRecord: string, idPatient: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${_.replace(environment.api_endpoints.medicalRecord, ':id', idPatient)}/${idMedicalRecord}`,
        { observe: 'response', headers }
      );
  }

  // [POST] create medical record
  public createMedicalRecord(medicalRecord: MedicalRecord, idPatient: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.post<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.medicalRecord, ':id', idPatient),
        medicalRecord, { observe: 'response', headers }
      );
  }

  // [PATCH] update medical record
  public updateMedicalRecord(medicalRecord: MedicalRecord, idPatient: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.patch<HttpResponse<object>>
      (
        environment.api_server + `${_.replace(environment.api_endpoints.medicalRecord, ':id', idPatient)}/${medicalRecord.id}`,
        medicalRecord, { observe: 'response', headers }
      );
  }

  // [PATCH] open medical record
  public openMedicalRecord(medicalRecordId: string, idPatient: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    const apiEndpoint = _.reduce(
      [[':idPatient', idPatient], [':id', medicalRecordId]],
      (res, [key, replacement]) => _.replace(res, key, replacement),
      environment.api_endpoints.medicalRecordOpen
    );

    return this.httpClient.patch<HttpResponse<object>>
      ( environment.api_server + apiEndpoint, {}, { observe: 'response', headers } );
  }

  // [GET] get medical record schedules
  public getMedicalRecordSchedules(idMedicalRecord: string, idPatient: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    const apiEndpoint = _.reduce(
      [[':idPatient', idPatient], [':id', idMedicalRecord]],
      (res, [key, replacement]) => _.replace(res, key, replacement),
      environment.api_endpoints.medicalRecordSchedules
    );

    return this.httpClient.get<HttpResponse<object>>
      ( environment.api_server + apiEndpoint, { observe: 'response', headers } );
  }

  // [GET] medical record quotes
  public getMedicalRecordQuotes(idPatient: string, idMedicalRecord: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.quotes}/patient/${idPatient}/medical-records/${idMedicalRecord}`,
        { observe: 'response', headers }
      );
  }

  // [GET] medical record documents
  public getMedicalRecordDocs(idPatient: string, idMedicalRecord: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${_.replace(environment.api_endpoints.medicalRecord, ':id', idPatient)}/${idMedicalRecord}/documents`,
        { observe: 'response', headers }
      );
  }

  // [POST] upload medical record document
  public uploadMedicalRecordDoc(patient: Patient, medicalRecord: MedicalRecord, docForm: Doc): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    return from(this.auxiliarService.generateMD5(docForm.document)).pipe(
      switchMap((md5sum: string) => {
        const formData = new FormData();
        formData.append('document', docForm.document);
        formData.append('md5sum', md5sum);
        formData.append('name', docForm.name);
        formData.append('description', docForm.description);

        return this.httpClient.post<HttpResponse<object>>(
          environment.api_server + `${_.replace(environment.api_endpoints.medicalRecord, ':id', patient.id)}/${medicalRecord.id}/document`,
          formData, { observe: 'response', headers }
        );
      })
    );
  }

  // [DELETE] delete medical record document
  public deleteMedicalRecordDoc(idPatient: string, medicalRecord: MedicalRecord, document: Doc): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.delete<HttpResponse<object>>(
      environment.api_server + `${_.replace(environment.api_endpoints.medicalRecord, ':id', idPatient)}/${medicalRecord.id}/document/${document.id}`,
      { observe: 'response', headers }
    );
  }

  // [GET] download medical record doc
  public downloadMedicalRecordDoc(medicalRecord: MedicalRecord, doc: Doc): Observable<HttpResponse<Blob>> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    const apiEndpoint = _.reduce(
      [[':id', medicalRecord.id], [':idDoc', doc.id]],
      (res, [key, replacement]) => _.replace(res, key, replacement),
      environment.api_endpoints.medicalRecordDocDownload
    );

    return this.httpClient.get(environment.api_server + apiEndpoint,
      { observe: 'response', headers, responseType: 'blob' });
  }

  // [POST] generate medical record doc
  public generateMedicalRecordDoc(document: any, medicalRecord: MedicalRecord, idPatient: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.post<HttpResponse<object>>(
      environment.api_server + `${environment.api_endpoints.patients}/${idPatient}/medical-records/${medicalRecord.id}/pdf-report`,
      document, { observe: 'response', headers }
    );
  }

  // open medical record detail
  public openMedicalRecordDetail(medicalRecord: MedicalRecord | undefined, patient: Patient, edit?: boolean): Promise<MedicalRecord> {
    return this.auxiliarService.openDrawer(MedicalRecordDrawerComponent, { 
      edit,
      medicalRecord,
      patient,
      position: 'left',
      iconLeft: 'pi pi-pencil',
      titleLeft: medicalRecord ? this.translate.instant('button.edit-medical-record') : this.translate.instant('button.new-medical-record'),
      width: '700px'
    });
  }

  // open medical record close form
  public openMedicalRecordCloseForm(medicalRecord: MedicalRecord, patient: Patient): Promise<MedicalRecord> {
    return this.auxiliarService.openDrawer(CloseMedicalRecordDrawerComponent, { 
      medicalRecord,
      patient,
      position: 'left',
      iconLeft: 'pi pi-lock',
      titleLeft: this.translate.instant('button.close-medical-record'),
      width: '700px'
    });
  }

  // open medical record new doc drawer
  public openDocDetail(patient: Patient | { id: string }, medicalRecord: MedicalRecord): Promise<any> {
    return this.auxiliarService.openDrawer(DocDrawerComponent, { 
      data: { patient, medicalRecord },
      position: 'right',
      iconRight: 'pi pi-file',
      titleRight: this.translate.instant('button.new-doc'),
      width: '600px'
    });
  }
}
